// Access Logs — live OLIVIUM sessions (operator, admin-gated surface).
//
// Wired to /api/square/command-center/sessions (GET) plus the
// /revoke and /lock POST routes. Every route is server-side,
// admin-gated (minLevel 80), and fails closed. Revoke/lock both
// carry an operator reason that is linked to an Olivium receipt
// server-side — this client never fabricates outcomes.
//
// Non-happy states handled explicitly: loading, auth-required
// (sign in), forbidden (operator access required), and any other
// error (honest warning banner).

import { useCallback, useEffect, useState } from 'react'
import {
  Button,
  DataGrid,
  Banner,
  InlineAlert,
  StatusBadge,
  Modal,
  FormField,
  Textarea,
} from '@square/ui'
import {
  api,
  ApiError,
  isAuthError,
  isForbiddenError,
  type CommandCenterSession,
} from '../../../api/client'
import EmptyState from '../../../components/EmptyState'
import RouteHeader from '../../RouteHeader'

// Map the free-form session.status string onto the canonical
// StatusBadge tones. Unknown statuses fall through to a neutral
// tone rather than inventing a meaning.
function statusTone(status: string): 'live' | 'pending' | 'offline' | 'critical' | 'info' {
  const s = (status || '').toLowerCase()
  if (s === 'active' || s === 'live') return 'live'
  if (s === 'locked' || s === 'suspended') return 'critical'
  if (s === 'revoked' || s === 'expired' || s === 'ended' || s === 'closed') return 'offline'
  if (s === 'pending' || s === 'idle') return 'pending'
  return 'info'
}

function formatStamp(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (isNaN(d.getTime())) return iso
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  const hh = String(d.getHours()).padStart(2, '0')
  const mi = String(d.getMinutes()).padStart(2, '0')
  return `${d.getFullYear()}-${mm}-${dd} ${hh}:${mi}`
}

// Shorten a long opaque identifier (user_id, device fingerprint)
// for the mono columns. Full value stays available on hover title.
function truncateId(value: string | null | undefined, head = 8, tail = 6): string {
  if (!value) return '—'
  if (value.length <= head + tail + 1) return value
  return `${value.slice(0, head)}…${value.slice(-tail)}`
}

const monoCell: React.CSSProperties = {
  display: 'inline-block',
  maxWidth: '100%',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  verticalAlign: 'middle',
}

const LOCK_REASON = 'operator lock'

export default function Sessions() {
  const [sessions, setSessions] = useState<CommandCenterSession[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [authRequired, setAuthRequired] = useState(false)
  const [forbidden, setForbidden] = useState(false)

  // Revoke modal state (reason is REQUIRED before the call fires).
  const [revokeTarget, setRevokeTarget] = useState<CommandCenterSession | null>(null)
  const [revokeReason, setRevokeReason] = useState('')

  // Lock confirmation state (reason is a fixed operator string).
  const [lockTarget, setLockTarget] = useState<CommandCenterSession | null>(null)

  const [busy, setBusy] = useState(false)

  function formatError(err: unknown): string {
    if (err instanceof ApiError) {
      if (!err.message || err.message === err.code) return err.code
      return `${err.code}: ${err.message}`
    }
    if (err instanceof Error) return err.message || 'request_failed'
    return String(err)
  }

  // Route any thrown error into the correct honest state. Returns
  // true when it was an auth/forbidden case the caller should stop on.
  const routeError = useCallback((err: unknown): boolean => {
    if (isAuthError(err)) {
      setAuthRequired(true)
      setForbidden(false)
      setError(null)
      setSessions([])
      return true
    }
    if (isForbiddenError(err) || (err instanceof ApiError && err.code === 'forbidden')) {
      setForbidden(true)
      setAuthRequired(false)
      setError(null)
      setSessions([])
      return true
    }
    setError(formatError(err))
    return false
  }, [])

  const fetchSessions = useCallback(async () => {
    setLoading(true)
    setError(null)
    setAuthRequired(false)
    setForbidden(false)
    try {
      const result = await api.commandCenter.sessions.list()
      setSessions(result.sessions || [])
    } catch (err) {
      routeError(err)
    } finally {
      setLoading(false)
    }
  }, [routeError])

  useEffect(() => { void fetchSessions() }, [fetchSessions])

  function closeRevoke() {
    setRevokeTarget(null)
    setRevokeReason('')
  }

  async function onRevokeConfirmed() {
    if (!revokeTarget) return
    const reason = revokeReason.trim()
    if (!reason) return // guarded by the disabled button, defensive here too
    setBusy(true)
    setError(null)
    try {
      await api.commandCenter.sessions.revoke({ session_id: revokeTarget.id, reason })
      closeRevoke()
      await fetchSessions()
    } catch (err) {
      const handled = routeError(err)
      if (handled) closeRevoke()
    } finally {
      setBusy(false)
    }
  }

  async function onLockConfirmed() {
    if (!lockTarget) return
    setBusy(true)
    setError(null)
    try {
      await api.commandCenter.sessions.lock({ session_id: lockTarget.id, reason: LOCK_REASON })
      setLockTarget(null)
      await fetchSessions()
    } catch (err) {
      const handled = routeError(err)
      if (handled) setLockTarget(null)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div>
      <RouteHeader
        kicker="SECURITY · ACCESS LOGS"
        title="Access Logs"
        description="Live OLIVIUM sessions. Revoke or lock any session under an operator reason — every action is linked to an Olivium receipt."
        crumbs={[
          { label: 'Command Center', href: '/command-center/overview' },
          { label: 'Access Logs' },
        ]}
        actions={
          <Button variant="ghost" size="sm" onClick={() => void fetchSessions()} loading={loading}>
            Refresh
          </Button>
        }
      />

      {error && (
        <div style={{ marginBottom: 14 }}>
          <Banner severity="warning" title="Access Logs">{error}</Banner>
        </div>
      )}

      {authRequired ? (
        <EmptyState
          title="Sign in to continue"
          body="Access Logs read live OLIVIUM sessions and require an authenticated operator session. Sign in at /auth/login to load, revoke, and lock sessions."
        />
      ) : forbidden ? (
        <EmptyState
          title="Operator access required"
          body="This is an admin-gated surface. Live session control (revoke / lock) is restricted to the Master Architect and authorized operators. Your account does not have operator access."
        />
      ) : loading ? (
        <InlineAlert severity="info">Loading live sessions…</InlineAlert>
      ) : (
        <DataGrid
          columns={[
            {
              id: 'user_id',
              header: 'USER',
              mono: true,
              width: 150,
              render: (r: CommandCenterSession) => (
                <span style={monoCell} title={r.user_id}>{truncateId(r.user_id)}</span>
              ),
            },
            {
              id: 'device_fingerprint',
              header: 'DEVICE',
              mono: true,
              width: 150,
              render: (r: CommandCenterSession) => (
                <span style={monoCell} title={r.device_fingerprint}>
                  {truncateId(r.device_fingerprint)}
                </span>
              ),
            },
            {
              id: 'ip_address',
              header: 'IP',
              mono: true,
              width: 130,
              render: (r: CommandCenterSession) => r.ip_address || '—',
            },
            {
              id: 'status',
              header: 'STATUS',
              width: 110,
              render: (r: CommandCenterSession) => (
                <StatusBadge tone={statusTone(r.status)}>{r.status || 'unknown'}</StatusBadge>
              ),
            },
            {
              id: 'last_seen_at',
              header: 'LAST SEEN',
              mono: true,
              width: 150,
              render: (r: CommandCenterSession) => formatStamp(r.last_seen_at),
            },
            {
              id: 'expires_at',
              header: 'EXPIRES',
              mono: true,
              width: 150,
              render: (r: CommandCenterSession) => formatStamp(r.expires_at),
            },
            {
              id: 'actions',
              header: '',
              width: 150,
              render: (r: CommandCenterSession) => (
                <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={busy}
                    onClick={() => { setLockTarget(r); setError(null) }}
                  >
                    Lock
                  </Button>
                  <Button
                    size="sm"
                    variant="danger"
                    disabled={busy}
                    onClick={() => { setRevokeTarget(r); setRevokeReason(''); setError(null) }}
                  >
                    Revoke
                  </Button>
                </div>
              ),
            },
          ]}
          rows={sessions}
          getRowId={(r: CommandCenterSession) => r.id}
          emptyMessage="No live sessions."
        />
      )}

      {/* Revoke — requires a typed operator reason before firing. */}
      <Modal
        open={!!revokeTarget}
        onClose={busy ? undefined : closeRevoke}
        title="Revoke session"
        width={460}
        footer={
          <>
            <Button variant="ghost" onClick={closeRevoke} disabled={busy}>Cancel</Button>
            <Button
              variant="danger"
              onClick={() => void onRevokeConfirmed()}
              loading={busy}
              disabled={busy || revokeReason.trim().length === 0}
            >
              Revoke
            </Button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--olv-text-secondary)' }}>
            {revokeTarget ? (
              <>
                Revoke the live session for user{' '}
                <span style={{ fontFamily: 'var(--font-mono)' }} title={revokeTarget.user_id}>
                  {truncateId(revokeTarget.user_id)}
                </span>
                {' '}on device{' '}
                <span style={{ fontFamily: 'var(--font-mono)' }} title={revokeTarget.device_fingerprint}>
                  {truncateId(revokeTarget.device_fingerprint)}
                </span>
                ? This immediately ends the session and cannot be undone.
              </>
            ) : null}
          </div>
          <FormField
            label="Operator reason"
            required
            help="Recorded on the Olivium receipt for this action. A reason is required."
          >
            <Textarea
              rows={3}
              value={revokeReason}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setRevokeReason(e.target.value)}
              placeholder="e.g. compromised device — operator-initiated revoke"
              disabled={busy}
              error={revokeReason.length > 0 && revokeReason.trim().length === 0}
            />
          </FormField>
        </div>
      </Modal>

      {/* Lock — fixed operator reason; simple confirm. */}
      <Modal
        open={!!lockTarget}
        onClose={busy ? undefined : () => setLockTarget(null)}
        title="Lock session"
        width={460}
        footer={
          <>
            <Button variant="ghost" onClick={() => setLockTarget(null)} disabled={busy}>Cancel</Button>
            <Button
              variant="primary"
              onClick={() => void onLockConfirmed()}
              loading={busy}
              disabled={busy}
            >
              Lock
            </Button>
          </>
        }
      >
        <div style={{ fontSize: 13, lineHeight: 1.5, color: 'var(--olv-text-secondary)' }}>
          {lockTarget ? (
            <>
              Lock the live session for user{' '}
              <span style={{ fontFamily: 'var(--font-mono)' }} title={lockTarget.user_id}>
                {truncateId(lockTarget.user_id)}
              </span>
              ? The session will be frozen under the operator reason{' '}
              <span style={{ fontFamily: 'var(--font-mono)' }}>"{LOCK_REASON}"</span> and linked to an
              Olivium receipt.
            </>
          ) : null}
        </div>
      </Modal>
    </div>
  )
}
